import { defineStore } from "pinia";
import { useRouter } from "vue-router";
import axios from "axios";
import { http } from "@/composable/httpService";
import { useHandleErrorStatus } from "@/composable/useHandleErrorStatus";
import Keycloak from "keycloak-js";
import { useAuth } from "@websanova/vue-auth/src/v3.js";
import Pusher from "pusher-js";
const { data } = await axios.get("/api/get-keycloak-env");
const keycloakConfig = {
  url: data.url,
  realm: data.realm,
  clientId: data.client_id,
  onLoad: data.onLoad,
  responseMode: data.responseMode,
  flow: data.keycloak_flow,
};

const keycloak = new Keycloak(keycloakConfig);

export const useAuthStore = defineStore({
  persist: true,
  id: "auth",
  state: () => ({
    // initialize state from local storage to enable user to stay logged in
    user: null,
    returnUrl: null,
    token: null,
    scormAccess: false,
    keycloak: null,
    isSSO: true,
    pusher: null,
    authProvider: null,
  }),
  actions: {
    async login(clientid) {
      // auth Route
      axios
        .post("/api/sso-post-login", {
          clientid,
        })
        .then(({ data }) => {
          this.isSSO = true;
          // update pinia state
          this.user = data.user;
          axios.defaults.headers.common.Authorization = `Bearer ${data.token}`;
          this.scormAccess = false;
          // store user details and jwt in local storage to keep user logged in between page refreshes
          localStorage.setItem("user", JSON.stringify(data.user));
          localStorage.setItem("bearer_token", JSON.stringify(data.token));

          // redirect to previous url or default to home page
          useRouter().push("/");
        })
        .catch(() => {
          window.location.reload();
        });
    },
    async logout() {
      const auth = useAuth();

      if (this.authProvider === 'auth0') {
        axios.get("/api/okta/logout").then(({ data }) => {
          auth.logout();
          localStorage.removeItem("nextUrl");
          window.location.href = data;
        });
      }

      if (this.authProvider === 'keycloak') {
        http().post("/sso-logout", { email: this.user.email });
        keycloak
          .logout()
          .then(() => {
            this.user = null;
            localStorage.removeItem("user");
            localStorage.removeItem("bearer_token");
            auth.logout();
            auth.user(null);
            auth.token(null);
          })
          .catch(() => {
            console.error("Keycloak logout failed");
          });
      }

      if (this.authProvider === 'web') {
        this.user = null;
        this.token = null;
        localStorage.removeItem("auth");
        localStorage.removeItem("auth_token_default");
        window.location.reload();
      }

    },
    async getAuth() {
      try {
        const response = await http().get("/auth/user");
        this.user = response.data;
      } catch (error) {
        useHandleErrorStatus(error);
      }
    },
    async initializeKeycloak() {
      const router = useRouter();
      const auth = useAuth();
      const store = this;
      keycloak.init({ onLoad: "login-required" }).then((authenticated) => {
        if (authenticated) {
          this.token = keycloak.token;
          axios
            .post("/api/keycloak-post-login", null, {
              headers: {
                Authorization: `Bearer ${this.token}`,
              },
            })
            .then(async ({ data }) =>  {
              let pusherKey = null;
              let pusherCluster = null;
              let alreadyLoggedOut = false;

              await axios.get("/api/get-env").then((response) => {
                pusherKey = response.data.pusher_key;
                pusherCluster = response.data.pusher_cluster;
              });

              store.pusher = new Pusher(pusherKey, {
                cluster: pusherCluster,
              });

              var channel = this.pusher.subscribe("logout-channel");

              channel.bind("App\\Events\\SSOLogout", (pusherData) => {
                if (pusherData.email === data.user.email && alreadyLoggedOut === false) {
                  store.logout();
                  alreadyLoggedOut = true;
                }
              });

              store.user = data.user;
              this.user = data.user;

              auth.user(data.user);
              auth.token(null, keycloak.token, false);
              this.token = keycloak.token;

              if (window.location.pathname === '/login') {
                router.push({ name: "home" });
              }
            })
            .catch((error) => {
              throw error;
            });
          // this.startTokenRefresh();
        } else {
          console.error("User is not authenticated");
        }
      });
    },
    async refreshToken() {
      try {
        keycloak
          .updateToken(70)
          .then((refreshed) => {
            if (refreshed) {
              this.token = keycloak.token;
            } else {
              console.warn(
                "Token not refreshed, valid for " +
                  Math.round(
                    keycloak.tokenParsed.exp +
                      keycloak.timeSkew -
                      new Date().getTime() / 1000
                  ) +
                  " seconds"
              );
            }
          })
          .catch(() => {
            console.error("Failed to refresh token");
          });
      } catch (error) {
        console.error("Failed to refresh token");
      }
    },
    startTokenRefresh() {
      setInterval(() => {
        this.refreshToken();
      }, 5000);
    },
  },
  getters: {
    fullName() {
      const name = this.user.name.split(" ");
      const capitalizeFirstLetter =
        name[0].charAt(0).toUpperCase() + name[0].slice(1);
      const endsWithS = capitalizeFirstLetter.endsWith("s");
      return endsWithS ? capitalizeFirstLetter : `${capitalizeFirstLetter}'s`;
    },
    isSuperAdmin() {
      if (!this.user?.id) {
        return false;
      }

      return this.user.roles.some(
        (role) => role.name === "super-administrator"
      );
    },
    isEnableNewHomePage() {
      return this.user.is_enabled_new_homepage;
    },
    isEnabledScormUpload() {
      return this.user.is_enabled_scorm_upload;
    },
    isScormAccess() {
      if (this.scormAccess) {
        return this.scormAccess;
      }
      return false;
    },
    isSingleUser() {
      if (!this.isSuperAdmin && this.user?.dealer?.id) {
        return Boolean(this.user.dealer.is_single_user);
      }

      return false;
    },
    isNotSingleRooftop() {
      if (!this.user) {
        return false;
      }

      if (!this.isSuperAdmin && this.user?.dealer?.is_automotive) {
        return false;
      }

      return true;
    },
    isDealerManager() {
      if (!this.user?.id) {
        return false;
      }

      return this.user.roles.some(
        (role) => role.name === "specific-dealer-manager"
      );
    },
    isSystemAdmin() {
      if (!this.user?.id) {
        return false;
      }

      return this.user.roles.some((role) => role.name === "system-admin");
    },
    isContentAdmin() {
      if (!this.user?.id) {
        return false;
      }

      return this.user.roles.some((role) => role.name === "content-admin");
    },
    isAccountManager() {
      if (!this.user?.id) {
        return false;
      }

      return this.user.roles.some((role) => role.name === "account-manager");
    },
    isCSM() {
      if (!this.user?.id) {
        return false;
      }

      return this.user.roles.some((role) => role.name === "csm");
    },
    isLearner() {
      if (!this.user?.id) {
        return false;
      }

      return this.user.roles.some((role) => role.name === "salesperson");
    },
    roles() {
      if (!this.user.id) {
        return [];
      }
      return this.user.roles.map((role) => role.name.replace("-", " "));
    },
    rolesFriendly() {
      if (!this.user.id) {
        return [];
      }
      return this.user.roles.map((role) => role.friendly_name);
    }
  },
});